<template>
  <Teleport to="body">
    <transition name="pwa-slide">
      <div v-if="visible" class="pwa-banner">
        <img :src="logoUrl" class="pwa-logo" alt="ISBET" />
        <div class="pwa-text">
          <strong>Instalar ISBET Dashboard</strong>
          <span>Adicione um atalho na tela inicial</span>
        </div>
        <button class="pwa-btn-install" @click="install">Instalar</button>
        <button class="pwa-btn-close" @click="dismiss" title="Fechar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import Swal from 'sweetalert2'
import { useLogo } from '@/composables/useLogo'

const DISMISSED_KEY = 'pwa_install_dismissed'

const { logoUrl } = useLogo()
const visible = ref(false)
let deferredPrompt: any = null

function canShow(): boolean {
  const dismissed = localStorage.getItem(DISMISSED_KEY)
  if (dismissed) {
    // Show again after 7 days
    const daysSince = (Date.now() - parseInt(dismissed)) / 86400000
    if (daysSince < 7) return false
  }
  const standalone =
    (window.navigator as any).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  return !standalone
}

function onBeforeInstall(e: Event) {
  e.preventDefault()
  deferredPrompt = e
  if (canShow()) visible.value = true
}

async function install() {
  if (deferredPrompt) {
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    deferredPrompt = null
    if (outcome === 'accepted') visible.value = false
  } else {
    // Fallback: iOS manual instructions
    Swal.fire({ title: 'Instalar no iOS', text: 'Toque no botão Compartilhar (⎋) e selecione "Adicionar à Tela de Início"', icon: 'info' })
    dismiss()
  }
}

function dismiss() {
  visible.value = false
  localStorage.setItem(DISMISSED_KEY, String(Date.now()))
}

onMounted(() => {
  window.addEventListener('beforeinstallprompt', onBeforeInstall)
  // iOS doesn't fire beforeinstallprompt — show generic banner after a delay
  setTimeout(() => {
    const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    if (isIos && canShow() && !deferredPrompt) visible.value = true
  }, 3000)
})

onUnmounted(() => {
  window.removeEventListener('beforeinstallprompt', onBeforeInstall)
})
</script>

<style scoped>
.pwa-banner {
  position: fixed;
  bottom: 0;
  left: 0; right: 0;
  z-index: 9999;
  background: #021627;
  border-top: 2px solid #BC0C24;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 -4px 24px rgba(0,0,0,.4);
}

.pwa-logo {
  width: 40px; height: 40px;
  border-radius: 10px;
  object-fit: contain;
  background: #fff;
  padding: 4px;
  flex-shrink: 0;
}

.pwa-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.pwa-text strong {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  font-family: 'Poppins', sans-serif;
}
.pwa-text span {
  font-size: 11.5px;
  color: rgba(255,255,255,.55);
  font-family: 'Poppins', sans-serif;
}

.pwa-btn-install {
  background: #BC0C24;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s;
}
.pwa-btn-install:active { background: #73010F; }

.pwa-btn-close {
  background: none;
  border: none;
  color: rgba(255,255,255,.4);
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pwa-btn-close:hover { color: rgba(255,255,255,.8); }

.pwa-slide-enter-active, .pwa-slide-leave-active {
  transition: transform .3s cubic-bezier(.4,0,.2,1);
}
.pwa-slide-enter-from, .pwa-slide-leave-to {
  transform: translateY(100%);
}
</style>
